import type {
    AnatomyBinding,
    AnatomyPart,
    AnatomyState,
    AnatomyVariant,
    ComponentAnatomySpec,
} from "./componentAnatomy";
import { anatomyTextStyle } from "./componentAnatomyText";

// Mined from designSystem/Tooltip/Tooltip.module.scss; bindings follow the
// spec's Tooltip table (docs/specs/2026-09-28-tooltip-figma-rework.md).

const parts: readonly AnatomyPart[] = [
    {
        id: "popup",
        label: "Popup",
        element: "Base UI Tooltip.Popup (.popup), translucent blurred surface",
    },
    { id: "title", label: "Title", element: "<p class=.title>, optional" },
    { id: "content", label: "Content", element: "<div class=.content>" },
    {
        id: "bullets",
        label: "Bullets",
        element: '<ul class=.bullets>, "•" drawn in li::before',
    },
    {
        id: "shortcut",
        label: "Shortcut",
        element: '<Shortcut size="md"> after the content',
    },
];

const states: readonly AnatomyState[] = [
    { id: "resting", label: "Open", selector: "" },
];

const sharedBindings: readonly AnatomyBinding[] = [
    { partId: "popup", property: "background", token: "--bg-translucent" },
    { partId: "popup", property: "border-width", token: "--border-width" },
    { partId: "popup", property: "border-color", token: "--border-divider" },
    {
        partId: "popup",
        property: "box-shadow",
        token: "--shadow-2",
        note: "Via the shadow-2-background-blur mixin (Figma Shadow-2-Background-Blur).",
    },
    {
        partId: "popup",
        property: "backdrop-filter",
        literal: "blur(10px)",
        note: "Same mixin; Figma's background blur radius 20 renders as CSS 10px.",
    },
    { partId: "popup", property: "border-radius", token: "--radius-m" },
    { partId: "popup", property: "padding-block", token: "--space-4" },
    { partId: "popup", property: "padding-inline", token: "--space-8" },
    { partId: "popup", property: "gap", token: "--space-4" },
    {
        partId: "popup",
        property: "max-width",
        literal: "min(var(--available-width), 300px)",
        via: "--tooltip-max-width",
        note: "ds-deviation: Figma's 300px width as a cap; shrinks to fit.",
    },
    ...anatomyTextStyle({
        scale: "xs",
        weight: "400",
        mixin: "text-xs-12-regular",
        partId: "content",
    }),
    { partId: "content", property: "color", token: "--fg-secondary" },
];

const titleBindings: readonly AnatomyBinding[] = [
    ...anatomyTextStyle({
        scale: "xs",
        weight: "500",
        mixin: "text-xs-12-medium",
        partId: "title",
    }),
    { partId: "title", property: "color", token: "--fg-secondary" },
];

const bulletBindings: readonly AnatomyBinding[] = [
    { partId: "bullets", property: "list-style", literal: "none" },
    {
        partId: "bullets",
        property: "padding-inline-start (li)",
        token: "--space-10",
        note: "Hanging indent for the ::before bullet (invented, PRO-12196 T6).",
    },
];

const shortcutRowBindings: readonly AnatomyBinding[] = [
    { partId: "popup", property: "gap", token: "--space-8" },
    { partId: "popup", property: "padding-inline-end", token: "--space-4" },
    { partId: "content", property: "white-space", literal: "nowrap" },
    {
        partId: "shortcut",
        property: "size",
        literal: "md (20px)",
        note: "Key chips per the Shortcut anatomy.",
    },
];

const variants: readonly AnatomyVariant[] = [
    {
        id: "text",
        label: "Text",
        selector: "",
        restingBindings: [],
        stateBindings: {},
    },
    {
        id: "title",
        label: "Title",
        selector: "",
        restingBindings: titleBindings,
        stateBindings: {},
    },
    {
        id: "titleBullets",
        label: "Title + bullets",
        selector: "",
        restingBindings: [...titleBindings, ...bulletBindings],
        stateBindings: {},
    },
    {
        id: "bullets",
        label: "Bullets",
        selector: "",
        restingBindings: bulletBindings,
        stateBindings: {},
    },
    {
        id: "shortcut",
        label: "Shortcut",
        selector: ".shortcutRow",
        restingBindings: shortcutRowBindings,
        stateBindings: {},
    },
    {
        id: "shortcutOnly",
        label: "Shortcut only",
        selector: ".shortcutOnly",
        restingBindings: [
            {
                partId: "popup",
                property: "padding-inline",
                token: "--space-4",
                note: "4px on every side (invented, PRO-12196 T11).",
            },
            { partId: "shortcut", property: "size", literal: "md (20px)" },
        ],
        stateBindings: {},
    },
];

export const tooltipAnatomy: ComponentAnatomySpec = {
    label: "Tooltip",
    schematic: "tooltip",
    rootSelector: ".popup",
    variantAxisLabel: "Figma variant",
    parts,
    states,
    sharedBindings,
    variants,
};
